import {
  Button,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  Paper,
  Radio,
  Typography,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Formik } from 'formik';
import moment from 'moment';
import React from 'react';
import { withRouter } from 'react-router-dom';

import { PaymentProvider } from '@aps-management/primapp-common';

import { Alert, CreditCardPreview, DialogPayment3DS } from '_components/elements';
import { CompleteCreditCardForm } from '_components/forms';

/* */
import i18n from '_utils/i18n';
import { createValidationSchema, validators } from '_utils/validation';

/* */
const validationSchema = createValidationSchema({
  cvv: validators.cvv,
  dueDate: validators.dateFourDigits,
});

/* */
const styles = (theme) => ({
  paper: {
    padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px`,
  },
  panel: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  radio: {
    padding: 0,
    marginRight: theme.spacing.unit * 2,
  },
  marginT: {
    marginTop: theme.spacing.unit * 2,
  },
});

/* */
class Payment extends React.Component {
  /* */
  static defaultProps = {
    amount: 5000,
    currency: 'EUR',
    display: 'row',
    numberOfInstallments: 1,
    defaultImprintUse: 'enabled',
    session3DS: null, // required
    // `scheduleMode` : l'écran ne règle rien, il met en place un échéancier. Le clic
    // n'envoie aucun débit (création du plan de prélèvement côté serveur, ou empreinte
    // d'autorisation pour enregistrer une nouvelle carte). Les 8 écrans de paiement réel
    // qui partagent ce composant ne passent pas cette prop et gardent leurs libellés.
    scheduleMode: false,
  };

  /* */
  constructor(props) {
    super(props);

    this.state = {
      expanded: null,
      // data 3DS
      error: null,
      sessionId: null,
      showIframe: false,
      // Désactivation visuelle des boutons pendant qu'un paiement est en cours.
      submitting: false,
    };

    this.id3D = null;
    this.form = React.createRef();
    this.imprintRequired = props.numberOfInstallments > 1;
    // Verrou SYNCHRONE anti double-soumission : effectif AVANT le prochain rendu React,
    // contrairement à disabled/setState (asynchrones). Un seul verrou pour les 2 moyens
    // de paiement (on ne règle pas par 2 moyens à la fois).
    this._paymentInFlight = false;
    this._isMounted = false;
  }

  /* */
  getGridItemProps() {
    const { display } = this.props;
    if (display === 'column') {
      return { xs: 12 };
    }

    return { xs: 12, sm: 9, md: 6 };
  }

  /* */
  componentDidMount() {
    const { imprint } = this.props;

    this._isMounted = true;
    this.setState({ expanded: imprint ? 'imprint' : 'other' });

    // add listener 3ds posted response
    window.addEventListener('message', this.handleMesssage3DS);
  }

  /* */
  componentDidUpdate(prevProps) {
    const { imprint } = this.props;

    if (prevProps.imprint !== imprint) this.setState({ expanded: imprint ? 'imprint' : 'other' });
  }

  /* */
  componentWillUnmount() {
    this._isMounted = false;
    // clean listener
    window.removeEventListener('message', this.handleMesssage3DS);
  }

  // Verrou de ré-entrée SYNCHRONE partagé par les 2 boutons de paiement. Empêche qu'un
  // double-tap (2 clics avant le re-render qui pose disabled) rejoue la mutation. Le flag
  // est relâché à la fin -> un retry légitime après échec ou après affichage du widget
  // reste possible (comportement voulu).
  handlePaymentTrigger = async (trigger) => {
    if (this._paymentInFlight) return;
    if (typeof trigger !== 'function') return;
    this._paymentInFlight = true;
    if (this._isMounted) this.setState({ submitting: true });
    try {
      await trigger();
    } finally {
      this._paymentInFlight = false;
      if (this._isMounted) this.setState({ submitting: false });
    }
  };

  /* */
  handleChange = (panel) => (event, expanded) => {
    this.setState({ expanded: expanded ? panel : false });
  };

  // Catch iframe 3DS message
  handleMesssage3DS = (event) => {
    if (!event.data.type || event.data.type !== 'response3DS') return;

    const { status, id3D } = event.data;

    if (status === 'success') {
      this.id3D = id3D;
      this.setState({ showIframe: false });
      this.form.current.props.submitForm();
    } else {
      this.handleError3DS();
    }
  };

  /* */
  handleError3DS() {
    this.id3D = null;
    this.form.current.props.setSubmitting(false);
    this.setState({
      showIframe: false,
      error: "Erreur d'authentification 3D Secure.",
    });
  }

  /* */
  // Libellé du bouton principal : en mode échéancier, aucun montant n'est prélevé, annoncer
  // « Payer <total> » induirait le membre en erreur.
  getSubmitLabel(isNewCard = false) {
    const { amount, scheduleMode } = this.props;

    if (!scheduleMode) return `Payer ${i18n.l('currency', amount / 100)}`;

    return isNewCard ? "Enregistrer ma carte et valider l'échéancier" : "Valider l'échéancier";
  }

  /* */
  getSubmitHint() {
    const { numberOfInstallments, scheduleMode } = this.props;

    if (numberOfInstallments <= 1) return null;
    if (!scheduleMode) return `Paiement en ${numberOfInstallments} mensualités`;

    return `Prélèvement en ${numberOfInstallments} mensualités, aucun débit lors de cet enregistrement`;
  }

  /* */
  renderCardImprint() {
    const { classes, imprint, payWithCardImprint } = this.props;
    const { submitting } = this.state;
    const submitHint = this.getSubmitHint();

    return (
      <React.Fragment>
        <Box display='flex' justifyContent='center'>
          <CreditCardPreview brand={imprint.brand} number={imprint.number} dueDate={imprint.dueDate} />
        </Box>
        {imprint.isExpired && (
          <React.Fragment>
            <Alert variant='error' className={classes.marginT}>
              {'La carte est expirée.'}
            </Alert>
            <Button
              fullWidth
              size='large'
              color='secondary'
              variant='outlined'
              className={classes.marginT}
              onClick={() => this.props.history.replace('/account/creditcard')}
            >
              {'Changer'}
            </Button>
          </React.Fragment>
        )}
        {imprint.isSoonExpired && (
          <Alert variant='warning' className={classes.marginT}>
            {'La carte arrive bientôt à expiration.'}
          </Alert>
        )}
        {!imprint.isExpired && (
          <React.Fragment>
            <Button
              fullWidth
              size='large'
              color='secondary'
              variant='contained'
              disabled={submitting}
              className={classes.marginT}
              onClick={() => this.handlePaymentTrigger(payWithCardImprint)}
            >
              {this.getSubmitLabel()}
            </Button>
            {submitHint && (
              <Typography component='p' align='center' variant='caption' className={classes.marginT}>
                {submitHint}
              </Typography>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  /* */
  renderCardImprintPanel() {
    const { classes, scheduleMode } = this.props;
    const { expanded } = this.state;
    const { imprintRequired } = this;

    // En mode échéancier les deux moyens cohabitent (valider avec la carte enregistrée OU
    // en enregistrer une autre) : il faut les panneaux dépliables pour pouvoir basculer.
    if (imprintRequired && !scheduleMode) {
      return (
        <Grid item {...this.getGridItemProps()}>
          <Paper className={classes.paper}>
            <Typography variant='subtitle1' component='h2'>
              {'Ma carte enregistrée'}
            </Typography>
            <br />
            {this.renderCardImprint()}
          </Paper>
        </Grid>
      );
    }

    return (
      <Grid item {...this.getGridItemProps()}>
        <ExpansionPanel expanded={expanded === 'imprint'} onChange={this.handleChange('imprint')}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Radio className={classes.radio} checked={expanded === 'imprint'} />
            <Typography variant='subtitle1' component='h2'>
              {'Ma carte enregistrée'}
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panel}>{this.renderCardImprint()}</ExpansionPanelDetails>
        </ExpansionPanel>
      </Grid>
    );
  }

  /* */
  getCardInfos = () => this.form.current.getExtraInfos();

  /* */
  onFormSubmit = (values, actions) => {
    const { amount, session3DS, payWithNewCard } = this.props;
    const { id3D } = this;

    const newValues = {
      ...values,
      ...this.getCardInfos(),
      id3D,
    };

    if (id3D !== null) {
      this.id3D = null; // force regénération pour les soumissions suivantes
      payWithNewCard(newValues, actions);
      return;
    }

    // build unique 3DS sessionId
    const sessionId = amount + Number(moment().format('x')) + Math.floor(Math.random() * 10 ** 12);

    // store temporarily data
    sessionStorage.setItem(
      `cart_${sessionId}`,
      JSON.stringify({
        ...session3DS,
        amount,
        id: sessionId,
        creditCard: {
          cvv: values.cvv,
          number: values.number.replace(/\s/g, ''),
          dueDate: values.dueDate.replace(/\D/g, ''),
        },
      }),
    );

    // load iframe 3DS
    this.setState({
      sessionId,
      error: null,
      showIframe: true,
    });
  };

  /* */
  renderNewCardForm() {
    const {
      imprint,
      golf,
      isPaylineProvider,
      defaultImprintUse,
      payWithNewCardPayline,
      scheduleMode,
      classes,
    } = this.props;

    const { error, saveImprint, submitting } = this.state;
    const { imprintRequired } = this;

    const shouldUsePayline = isPaylineProvider === true || PaymentProvider.canUsePayline(golf);
    const isPaylineWithoutContract = !shouldUsePayline && PaymentProvider.isPayline(golf);

    if (isPaylineWithoutContract) {
      return (
        <React.Fragment>
          <Alert variant='error'>
            {'Paiement en ligne temporairement indisponible. Veuillez réessayer ultérieurement ou contacter le golf.'}
          </Alert>
        </React.Fragment>
      );
    }

    if (shouldUsePayline) {
      return (
        <React.Fragment>
          {error && <Alert variant='error'>{error}</Alert>}
          {!imprintRequired && !scheduleMode && (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type='checkbox'
                  checked={saveImprint}
                  onChange={(e) => this.setState({ saveImprint: e.target.checked })}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontSize: 14, color: '#666' }}>
                  {
                    "J'autorise que mes coordonnées bancaires soient sauvegardées sur la plateforme sécurisée de notre banque partenaire."
                  }
                </span>
              </label>
            </div>
          )}
          <Button
            fullWidth
            size='large'
            color='secondary'
            variant='contained'
            disabled={submitting}
            className={classes.marginT}
            onClick={() =>
              this.handlePaymentTrigger(() => {
                // En mode échéancier l'enregistrement de la carte n'est pas optionnel :
                // sans wallet, aucun prélèvement ne pourra être exécuté.
                sessionStorage.setItem('payline_saveImprint', saveImprint || scheduleMode ? '1' : '0');
                return payWithNewCardPayline();
              })
            }
          >
            {this.getSubmitLabel(true)}
          </Button>
          {this.getSubmitHint() && (
            <Typography component='p' align='center' variant='caption' className={classes.marginT}>
              {this.getSubmitHint()}
            </Typography>
          )}
        </React.Fragment>
      );
    }

    // Aucun PSP : le golf n'a pas de contrat VADS Payline. Le formulaire de saisie
    // PAN qui servait de repli passait par `paylineSubscribe` (createToken seul,
    // AUCUN wallet créé). La carte enregistrée par cette voie n'est pas prélevable :
    // elle produisait un échéancier qui échouait à chaque échéance, et le filet
    // serveur lui inventait même un `wallet_id` (cf. audit du 28/07, chaîne 2).
    // Paybox, l'autre repli historique, a été retiré. On annonce donc l'indisponibilité.
    return (
      <React.Fragment>
        <Alert variant='error'>
          {'Le paiement par carte n’est pas disponible pour ce golf. Rapprochez-vous de l’accueil.'}
        </Alert>
      </React.Fragment>
    );
  }

  /* */
  renderNewCardPanel() {
    const { classes, imprint, scheduleMode } = this.props;
    const { expanded } = this.state;

    return (
      <Grid item {...this.getGridItemProps()}>
        <ExpansionPanel expanded={expanded === 'other'} onChange={this.handleChange('other')}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Radio className={classes.radio} checked={expanded === 'other'} />
            <Typography variant='subtitle1' component='h2'>
              {!imprint ? 'Nouvelle carte bancaire' : scheduleMode ? 'Utiliser une autre carte' : 'Autre carte bancaire'}
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panel}>{this.renderNewCardForm()}</ExpansionPanelDetails>
        </ExpansionPanel>
      </Grid>
    );
  }

  /* */
  renderPanels() {
    const { imprint, scheduleMode } = this.props;
    const { imprintRequired } = this;

    return (
      <React.Fragment>
        {imprint && this.renderCardImprintPanel()}
        {/* En mode échéancier, le membre doit toujours pouvoir enregistrer une autre carte :
            `imprintRequired` masquait ce panneau dès qu'une carte à wallet existait. */}
        {(!imprint || !imprintRequired || scheduleMode) && this.renderNewCardPanel()}
      </React.Fragment>
    );
  }

  /* */
  renderDialog3DS() {
    const { showIframe, sessionId } = this.state;

    if (!sessionId) return null;

    return <DialogPayment3DS open={showIframe} sessionId={sessionId} onClose={() => this.handleError3DS()} />;
  }

  /* */
  render() {
    const { currency } = this.props;

    // Seule la devise EUR est acceptée
    if (currency !== 'EUR') {
      return (
        <Alert variant='warning'>
          {`Actuellement, la devise ${currency} n'est pas acceptée pour le paiement en ligne.`}
        </Alert>
      );
    }

    return (
      <React.Fragment>
        <Grid
          container
          spacing={40}
          justify='center'
          style={{
            border: 'red 1px',
          }}
        >
          {this.renderPanels()}
        </Grid>
        {this.renderDialog3DS()}
      </React.Fragment>
    );
  }
}

const PaymentWithRouter = withRouter(Payment);

export default withStyles(styles)(PaymentWithRouter);
